import endOfDay from 'date-fns/endOfDay'
import isBefore from 'date-fns/isBefore'
import startOfDay from 'date-fns/startOfDay'
import { isArray, min } from 'lodash'
import { IntlShape, defineMessages } from 'react-intl'
import * as yup from 'yup'

import { getFrequencyDaysCount, vehicleHasDefaultFrequency } from 'models/services'
import { EVehicleScheduleType, IVehicle, IVehiclesForm } from './types'
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays'
import isAfter from 'date-fns/isAfter'
import { formatISODate } from 'utils/date'
import { gcd } from 'utils/math'
import addDays from 'date-fns/addDays'

const messages = defineMessages({
  required: {
    id: 'dictionary.required',
    defaultMessage: 'Required'
  },
  requiredVehicle: {
    id: 'service.form.vehicle.required',
    defaultMessage: 'The vehicle is required'
  },
  requiredStartDate: {
    id: 'service.form.startDate.required',
    defaultMessage: 'The start date is required'
  },
  startDateAfterPreviousExpirationDate: {
    id: 'service.form.startDateBeforeExpirationDate',
    defaultMessage: 'Start date before previous expiration date'
  },
  expirationDateBeforeNextStartDate: {
    id: 'service.form.expirationDateBeforeNextStartDate',
    defaultMessage: 'Expiration date after next start date'
  },
  vehiclesWithCollision: {
    id: 'service.form.vehiclesWithCollision',
    defaultMessage: 'Collision'
  }
})

const getVehiclesByContext = (context: yup.TestContext<IVehiclesForm>): IVehicle[] => {
  // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
  const { value } = context.from.find(({ value }) => isArray(value.vehicles))
  return value.vehicles
}

const getCurrentVehicleIndexByPath = (path: string): number => {
  return parseInt(path.split('vehicles[')[1].split(']')[0], 10)
}

const getNextVehicleWithOneSharedOperationDay = (
  vehicles: IVehicle[],
  currentVehicleIndex: number
): IVehicle | undefined => {
  const currentVehicle = vehicles[currentVehicleIndex]
  return vehicles.find((vehicle, index) => {
    if (index <= currentVehicleIndex) {
      return false
    }
    return vehicle.operationDays.some((operationDay) => currentVehicle.operationDays.includes(operationDay))
  })
}

const getPreviousVehicleWithOneSharedOperationDay = (
  vehicles: IVehicle[],
  currentVehicleIndex: number
): IVehicle | undefined => {
  const reversedVehicleIndex = vehicles.length - currentVehicleIndex - 1
  const reversedVehicles = [...vehicles].reverse()
  return getNextVehicleWithOneSharedOperationDay(reversedVehicles, reversedVehicleIndex)
}

const getMinEndDateBetweenVehicles = (vehicleLeft: IVehicle, vehicleRight: IVehicle): Date | undefined => {
  if (!vehicleLeft.expirationDate && !vehicleRight.expirationDate) {
    return undefined
  }
  if (!vehicleLeft.expirationDate) {
    return vehicleRight.expirationDate
  }
  if (!vehicleRight.expirationDate) {
    return vehicleLeft.expirationDate
  }
  return min([vehicleLeft.expirationDate, vehicleRight.expirationDate]) as Date
}

const hasCollisionBetweenVehiclesScheduleFrequency = (vehicleLeft: IVehicle, vehicleRight: IVehicle): boolean => {
  let daysDifference: number

  if (!vehicleLeft.startDate || !vehicleRight.startDate) return true

  if (isAfter(vehicleLeft.startDate, vehicleRight.startDate)) {
    daysDifference = Math.abs(differenceInCalendarDays(vehicleLeft.startDate, vehicleRight.startDate))
  } else {
    daysDifference = Math.abs(differenceInCalendarDays(vehicleRight.startDate, vehicleLeft.startDate))
  }
  const frequencyInDaysLeft = getFrequencyDaysCount(vehicleLeft.frequency)
  const frequencyInDaysRight = getFrequencyDaysCount(vehicleRight.frequency)
  const gcdForFrequencies = gcd(frequencyInDaysLeft, frequencyInDaysRight)

  if (daysDifference % gcdForFrequencies !== 0) {
    return false
  }

  const minEndDate = getMinEndDateBetweenVehicles(vehicleLeft, vehicleRight)
  if (!minEndDate) {
    return true
  }

  const dateMap: Record<string, boolean> = {}

  let startDate = vehicleLeft.startDate
  while (isBefore(startDate, minEndDate)) {
    dateMap[formatISODate(startDate)] = true
    startDate = addDays(startDate, frequencyInDaysLeft)
  }

  startDate = vehicleRight.startDate
  while (isBefore(startDate, minEndDate)) {
    if (dateMap[formatISODate(startDate)]) {
      return true
    }
    startDate = addDays(startDate, frequencyInDaysRight)
  }
  return false
}

const hasCollision = (vehicles: IVehicle[], currentVehicleIndex: number): boolean => {
  const currentVehicle = vehicles[currentVehicleIndex]
  return vehicles.some((vehicle, index) => {
    if (index === currentVehicleIndex) return false
    if (!currentVehicle.frequency.value || !vehicle.frequency.value) return false
    return hasCollisionBetweenVehiclesScheduleFrequency(currentVehicle, vehicle)
  })
}

export const vehiclesSchema = (intl: IntlShape): yup.SchemaOf<IVehiclesForm> => {
  return yup
    .object()
    .shape({
      vehicles: yup
        .array()
        .of(
          yup.object().shape({
            id: yup.string().optional(),
            vehicle: yup.object().shape({
              id: yup.string().required(intl.formatMessage(messages.requiredVehicle))
            }),
            startDate: yup
              .date()
              .test(
                'is-start-date-after-previous-expiration-date',
                intl.formatMessage(messages.startDateAfterPreviousExpirationDate),
                // @ts-expect-error FIXME
                function (startDate, context: yup.TestContext<IVehiclesForm>) {
                  const vehicles = getVehiclesByContext(context)
                  if (vehicles[0].type === EVehicleScheduleType.Frequency) return true

                  const currentVehicleIndex = getCurrentVehicleIndexByPath(context.path)
                  const previousVehicle = getPreviousVehicleWithOneSharedOperationDay(vehicles, currentVehicleIndex)
                  if (previousVehicle && vehicleHasDefaultFrequency(previousVehicle)) {
                    if (previousVehicle.expirationDate && startDate) {
                      return isBefore(endOfDay(previousVehicle.expirationDate), startOfDay(startDate))
                    } else if (!previousVehicle.expirationDate) {
                      return false
                    }
                  }
                  return true
                }
              )
              .required(intl.formatMessage(messages.requiredStartDate)),
            expirationDate: yup
              .date()
              .test(
                'is-expiration-date-after-previous-start-date',
                intl.formatMessage(messages.expirationDateBeforeNextStartDate),
                // @ts-expect-error FIXME
                function (expirationDate, context: yup.TestContext<IVehiclesForm>) {
                  const vehicles = getVehiclesByContext(context)

                  if (vehicles[0].type === EVehicleScheduleType.Frequency) return true

                  const currentVehicleIndex = getCurrentVehicleIndexByPath(context.path)
                  const nextVehicle = getNextVehicleWithOneSharedOperationDay(vehicles, currentVehicleIndex)
                  if (nextVehicle?.startDate) {
                    if (expirationDate) {
                      return isBefore(endOfDay(expirationDate), startOfDay(nextVehicle.startDate))
                    }
                    return false
                  }
                  return true
                }
              )
              .optional(),
            frequency: yup
              .object()
              .shape({
                value: yup.mixed(),
                unit: yup.string().required()
              })
              .test(
                'has-collision',
                intl.formatMessage(messages.vehiclesWithCollision),
                // @ts-expect-error FIXME
                function (_, context: yup.TestContext<IVehiclesForm>) {
                  const vehicles = getVehiclesByContext(context)
                  if (vehicles[0].type === EVehicleScheduleType.Dow) return true
                  const currentVehicleIndex = getCurrentVehicleIndexByPath(context.path)
                  return !hasCollision(vehicles, currentVehicleIndex)
                }
              )
              .test('has-value', intl.formatMessage(messages.required), function (frequency) {
                const value = frequency.value as number
                return isFinite(value) && value > 0
              })
          })
        )
        .required()
    })
    .required()
}
